import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
// import { adminAPI } from '../../../services/api';
import { teacherAPI as adminAPI } from '../../../services/api';
import { toast } from 'react-hot-toast';
import Modal from 'react-modal';

if (typeof window !== 'undefined') {
  Modal.setAppElement('#root');
}

const StaffQuizDetails = () => {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQuestions, setShowQuestions] = useState(false);
  const [showAssignedStudents, setShowAssignedStudents] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Assigned students selection states
  const [selectedAssignments, setSelectedAssignments] = useState([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchQuiz();
  }, [quizId]);

  const fetchQuiz = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getQuiz(quizId);
      setQuiz(response.data);
      // Reset selections when quiz data refreshes
      setSelectedAssignments([]);
    } catch (error) {
      toast.error('Failed to load quiz details');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = async () => {
    const newStatus = quiz.status === 'active' ? 'draft' : 'active';
    try {
      await adminAPI.toggleQuizStatus(quizId, newStatus);
      setQuiz({ ...quiz, status: newStatus });
      toast.success(`Quiz ${newStatus === 'active' ? 'published' : 'drafted'}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this quiz? This action cannot be undone.')) return;
    try {
      await adminAPI.deleteQuiz(quizId);
      toast.success('Quiz deleted');
      navigate('/teacher/quizzes');
    } catch (error) {
      toast.error('Failed to delete quiz');
    }
  };

  // Assigned students management functions
  const handleSelectAssignment = (assignmentId) => {
    setSelectedAssignments(prev => 
      prev.includes(assignmentId) 
        ? prev.filter(id => id !== assignmentId)
        : [...prev, assignmentId]
    );
  };

  const handleSelectAll = () => {
    if (selectedAssignments.length === filteredStudents.length) {
      setSelectedAssignments([]);
    } else {
      setSelectedAssignments(filteredStudents.map(s => s.id));
    }
  };

  const handleRemoveSingle = async (studentId, studentName) => {
    if (!window.confirm(`Remove ${studentName} from this quiz?`)) return;
    
    setIsDeleting(true);
    try {
      await adminAPI.removeQuizAssignment(quizId, studentId);
      toast.success('Student removed from quiz');
      await fetchQuiz(); // Refresh data
    } catch (error) {
      toast.error('Failed to remove student');
      console.error(error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedAssignments.length === 0) return;
    
    setIsDeleting(true);
    try {
      await adminAPI.removeQuizAssignmentsBulk(quizId, selectedAssignments);
      toast.success(`Removed ${selectedAssignments.length} student(s) from quiz`);
      setSelectedAssignments([]);
      await fetchQuiz(); // Refresh data
    } catch (error) {
      toast.error('Failed to remove students');
      console.error(error);
    } finally {
      setIsDeleting(false);
      setShowBulkDeleteConfirm(false);
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm('Are you sure you want to remove ALL students from this quiz? This action cannot be undone.')) return;
    
    setIsDeleting(true);
    try {
      await adminAPI.removeAllQuizAssignments(quizId);
      toast.success('Removed all students from quiz');
      await fetchQuiz(); // Refresh data
    } catch (error) {
      toast.error('Failed to remove students');
      console.error(error);
    } finally {
      setIsDeleting(false);
    }
  };

  const getStatusBadge = () => {
    return quiz?.status === 'active'
      ? 'bg-green-100 text-green-800 border-green-200'
      : 'bg-yellow-100 text-yellow-800 border-yellow-200';
  };

  // Filter assigned students based on search term
  const filteredStudents = quiz?.assigned_students?.filter(student => 
    student.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.student_reg?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!quiz) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Quiz not found</p>
        <Link to="/teacher/quizzes" className="text-blue-600 hover:underline mt-2 inline-block">
          Back to Quizzes
        </Link>
      </div>
    );
  }

  const answeredCount = quiz.attempts?.filter(a => a.status === 'completed').length || 0;
  const inProgressCount = quiz.attempts?.filter(a => a.status === 'in_progress').length || 0;
  const avgScore = quiz.attempts?.filter(a => a.score !== null).reduce((sum, a) => sum + a.score, 0) / (quiz.attempts?.filter(a => a.score !== null).length || 1);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <Link to="/teacher/quizzes" className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1 mb-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Quizzes
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">{quiz.title}</h1>
          <p className="text-gray-600 mt-1">{quiz.description || 'No description'}</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleStatusToggle}
            className={`px-4 py-2 rounded-lg font-medium transition ${quiz.status === 'active' ? 'bg-yellow-500 hover:bg-yellow-600 text-white' : 'bg-green-500 hover:bg-green-600 text-white'}`}
          >
            {quiz.status === 'active' ? 'Set Draft' : 'Publish'}
          </button>
          <button
            onClick={handleDelete}
            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
          >
            Delete
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow p-6 border border-gray-100">
          <p className="text-sm text-gray-600">Questions</p>
          <p className="text-2xl font-bold text-gray-900">{quiz.questions?.length || 0}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-6 border border-gray-100">
          <p className="text-sm text-gray-600">Assigned Students</p>
          <p className="text-2xl font-bold text-blue-600">{quiz.assigned_students?.length || 0}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-6 border border-gray-100">
          <p className="text-sm text-gray-600">Total Attempts</p>
          <p className="text-2xl font-bold text-purple-600">{quiz.attempts?.length || 0}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-6 border border-gray-100">
          <p className="text-sm text-gray-600">Avg Score</p>
          <p className="text-2xl font-bold text-green-600">{Math.round(avgScore)}</p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Link
          to={`/teacher/quizzes/${quizId}/import-questions`}
          className="bg-indigo-50 hover:bg-indigo-100 p-4 rounded-xl flex items-center gap-3 transition group"
        >
          <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center group-hover:bg-indigo-200">
            <svg className="w-5 h-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.4１4z" clipRule="evenodd"/>
            </svg>
          </div>
          <div>
            <p className="font-medium text-gray-900">Import Questions</p>
            <p className="text-sm text-gray-500">Upload .cbthost package</p>
          </div>
        </Link>

        <Link
          to={`/teacher/quizzes/${quizId}/assign`}
          className="bg-blue-50 hover:bg-blue-100 p-4 rounded-xl flex items-center gap-3 transition group"
        >
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200">
            <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
              <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z"/>
            </svg>
          </div>
          <div>
            <p className="font-medium text-gray-900">Assign to Students</p>
            <p className="text-sm text-gray-500">Select students or upload file</p>
          </div>
        </Link>

        <Link
          to={`/teacher/quizzes/${quizId}/attempts`}
          className="bg-purple-50 hover:bg-purple-100 p-4 rounded-xl flex items-center gap-3 transition group"
        >
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center group-hover:bg-purple-200">
            <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z"/>
            </svg>
          </div>
          <div>
            <p className="font-medium text-gray-900">View Attempts</p>
            <p className="text-sm text-gray-500">See student performance</p>
          </div>
        </Link>
      </div>

      {/* Assigned Students Section */}
      <div className="bg-white rounded-xl shadow border border-gray-100">
        <div className="p-6 border-b border-gray-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Assigned Students</h2>
            <p className="text-sm text-gray-500">{quiz.assigned_students?.length || 0} students assigned to this quiz</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {selectedAssignments.length > 0 && (
              <button
                onClick={() => setShowBulkDeleteConfirm(true)}
                disabled={isDeleting}
                className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm font-medium disabled:opacity-50"
              >
                Remove Selected ({selectedAssignments.length})
              </button>
            )}
            {quiz.assigned_students?.length > 0 && (
              <button
                onClick={handleDeleteAll}
                disabled={isDeleting}
                className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium disabled:opacity-50"
              >
                Remove All
              </button>
            )}
            <button
              onClick={() => setShowAssignedStudents(!showAssignedStudents)}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium px-3 py-1.5"
            >
              {showAssignedStudents ? 'Hide' : 'Show'} Students
            </button>
          </div>
        </div>

        {showAssignedStudents && (
          <div className="p-6">
            {/* Search Bar */}
            <div className="mb-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by student name or registration number..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <svg className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            {filteredStudents.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">No students found</p>
                {searchTerm && <p className="text-sm text-gray-400 mt-1">Try a different search term</p>}
              </div>
            ) : (
              <>
                <div className="mb-3 flex justify-between items-center">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedAssignments.length === filteredStudents.length && filteredStudents.length > 0}
                      onChange={handleSelectAll}
                      className="w-4 h-4 text-blue-600 rounded cursor-pointer"
                    />
                    <span className="text-sm text-gray-600">Select All ({filteredStudents.length})</span>
                  </label>
                  {selectedAssignments.length > 0 && (
                    <span className="text-sm text-blue-600">{selectedAssignments.length} selected</span>
                  )}
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                          <input
                            type="checkbox"
                            checked={selectedAssignments.length === filteredStudents.length && filteredStudents.length > 0}
                            onChange={handleSelectAll}
                            className="w-4 h-4 text-blue-600 rounded"
                          />
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Registration Number</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredStudents.map((student) => (
                        <tr key={student.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4">
                            <input
                              type="checkbox"
                              checked={selectedAssignments.includes(student.id)}
                              onChange={() => handleSelectAssignment(student.id)}
                              className="w-4 h-4 text-blue-600 rounded"
                            />
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-medium">
                                {student.student_name?.charAt(0) || '?'}
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">{student.student_name}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {student.student_reg}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              student.status === 'assigned' 
                                ? 'bg-blue-100 text-blue-800'
                                : student.status === 'started'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {student.status || 'Assigned'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {student.due_date ? new Date(student.due_date).toLocaleDateString() : 'No due date'}
                          </td>
                          <td className="px-6 py-4 text-sm font-medium">
                            <button
                              onClick={() => handleRemoveSingle(student.id, student.student_name)}
                              disabled={isDeleting}
                              className="text-red-600 hover:text-red-900 disabled:opacity-50"
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {filteredStudents.length > 0 && (
              <div className="mt-4 text-center text-sm text-gray-500">
                Showing {filteredStudents.length} of {quiz.assigned_students?.length || 0} students
              </div>
            )}
          </div>
        )}
      </div>

      {/* Questions Section */}
      <div className="bg-white rounded-xl shadow border border-gray-100">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Questions</h2>
            <p className="text-sm text-gray-500">{quiz.questions?.length || 0} questions in this quiz</p>
          </div>
          <button
            onClick={() => setShowQuestions(!showQuestions)}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            {showQuestions ? 'Hide' : 'Show'} Questions
          </button>
        </div>

        {showQuestions && (
          <div className="p-6 space-y-4">
            {quiz.questions?.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">No questions yet. Import questions to get started.</p>
                <Link
                  to={`/teacher/quizzes/${quizId}/import-questions`}
                  className="inline-block mt-4 text-blue-600 hover:text-blue-800"
                >
                  Import Questions →
                </Link>
              </div>
            ) : (
              quiz.questions.map((q, index) => (
                <div
                  key={q.id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition cursor-pointer"
                  onClick={() => setSelectedQuestion(q)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-medium text-gray-500">Q{index + 1}</span>
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                          {q.question_type}
                        </span>
                        <span className="text-xs text-gray-500">{q.marks} mark{q.marks !== 1 ? 's' : ''}</span>
                      </div>
                      <div
                        className="text-gray-800 prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: q.question_text }}
                      />
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Question Detail Modal */}
      <Modal
        isOpen={!!selectedQuestion}
        onRequestClose={() => setSelectedQuestion(null)}
        style={{
          overlay: { backgroundColor: 'rgba(0,0,0,0.5)' },
          content: { maxWidth: '800px', margin: 'auto', padding: '0', borderRadius: '1rem', overflow: 'hidden' }
        }}
      >
        {selectedQuestion && (
          <div className="bg-white">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex justify-between items-center">
              <h3 className="text-white font-bold">Question Details</h3>
              <button onClick={() => setSelectedQuestion(null)} className="text-white hover:text-gray-200">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              <div className="mb-4 flex gap-2">
                <span className="text-sm bg-gray-100 text-gray-600 px-2 py-1 rounded">{selectedQuestion.question_type}</span>
                <span className="text-sm bg-blue-100 text-blue-600 px-2 py-1 rounded">{selectedQuestion.marks} marks</span>
              </div>
              <div
                className="text-gray-800 mb-6 prose max-w-none"
                dangerouslySetInnerHTML={{ __html: selectedQuestion.question_text }}
              />
              
              {selectedQuestion.options && selectedQuestion.options.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-medium text-gray-800 mb-3">Options:</h4>
                  <div className="space-y-2">
                    {selectedQuestion.options.map((opt, idx) => (
                      <div
                        key={idx}
                        className={`p-3 border rounded-lg ${opt.status === 'correct' ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}
                      >
                        <div className="flex items-start gap-3">
                          <span className="font-bold text-gray-700 min-w-6">{opt.label}.</span>
                          <span dangerouslySetInnerHTML={{ __html: opt.text }} />
                          {opt.status === 'correct' && (
                            <span className="ml-auto text-xs bg-green-500 text-white px-2 py-0.5 rounded">Correct</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedQuestion.correct_answer && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="font-medium text-green-800 mb-1">Correct Answer:</p>
                  <p className="text-green-700">{selectedQuestion.correct_answer}</p>
                </div>
              )}

              {selectedQuestion.explanation && (
                <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="font-medium text-blue-800 mb-1">Explanation:</p>
                  <p className="text-blue-700">{selectedQuestion.explanation}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Bulk Delete Confirmation Modal */}
      <Modal
        isOpen={showBulkDeleteConfirm}
        onRequestClose={() => setShowBulkDeleteConfirm(false)}
        style={{
          overlay: { backgroundColor: 'rgba(0,0,0,0.5)' },
          content: { maxWidth: '400px', margin: 'auto', padding: '0', borderRadius: '1rem', overflow: 'hidden' }
        }}
      >
        <div className="bg-white p-6">
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900">Remove Students</h3>
          </div>
          <p className="text-gray-600 mb-6">
            Are you sure you want to remove <strong>{selectedAssignments.length}</strong> student(s) from this quiz? This action cannot be undone.
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowBulkDeleteConfirm(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleBulkDelete}
              disabled={isDeleting}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {isDeleting ? 'Removing...' : 'Remove Students'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default StaffQuizDetails;